/**
 * CORE-BL — Guided Onboarding & Product Education · route reachability
 *
 * Onboarding cannot teach a role on a page the product refuses to let them
 * open. A lesson pointing at `/` is not instruction for a dispatcher — the
 * router answers that route with a 403 page, so the step is an invitation to
 * a door that does not open.
 *
 * Why this file exists rather than a route change
 * -----------------------------------------------
 * Route authority lives in `App.tsx` and is **owned by another lane**. CORE-BL
 * does not widen access to fix its own content problem — that would be
 * onboarding granting itself permissions, which is the inverse of the
 * presentation-not-authorization rule this capability already follows.
 *
 * So this is a deliberate MIRROR of every `requiredRoles` guard in `App.tsx`,
 * and `coreBlRouteReachability.test.ts` re-parses `App.tsx` and fails on ANY
 * difference — a route added, removed, or re-scoped. The router remains the
 * authority; this is a checked read of it that the resolver can consult
 * synchronously.
 *
 * The whole table is mirrored rather than only the routes onboarding happens
 * to reference today. A partial mirror silently answers "reachable" for every
 * route it omits, and that is exactly how the first version of this file let a
 * dispatcher be offered a lesson on `/customers`.
 *
 * What it produces
 * ----------------
 * Steps whose page a role cannot open are filtered out. If that empties a
 * track, the person is told plainly that their instruction depends on access
 * they do not have — which is honest, and is not the same as being shown an
 * empty tour.
 *
 * GENERATED FROM `App.tsx`. Do not hand-edit: change the router, then update
 * this from it and let the drift test confirm the two agree.
 */

import type { AppRole, KnownAppRole } from './onboardingExperience';

/**
 * Every `requiredRoles` guard in `App.tsx`, mirrored exactly.
 *
 * A path absent from this table has no role guard on it.
 */
export const ROUTE_ROLES: Readonly<Record<string, ReadonlyArray<AppRole>>> =
  Object.freeze({
    '/': ['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN'],
    '/account-settings': ['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN'],
    '/addon-suggestion-settings': ['MANAGER', 'OWNER', 'ORG_ADMIN'],
    '/vehicle-duplicates': ['MANAGER', 'OWNER', 'ORG_ADMIN'],
    '/admin/floo-network': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/admin/optinetwork': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/admin/optinetwork/map': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/advisor-passport/:userId': ['MANAGER', 'OWNER'],
    '/alpha-testers': ['MANAGER', 'OWNER'],
    '/analytics': ['OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'],
    // CORE-BQ Slice B: the shop-facing integrity surface. Read-only, and
    // gated to the roles that can act on a contradiction.
    '/operational-integrity': ['OWNER', 'MANAGER', 'ORG_ADMIN'],
    '/assignment-debug': ['OWNER', 'MANAGER', 'SERVICE_WRITER'],
    '/attach': ['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN'],
    '/birdseye': ['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN'],
    '/bug-reports': ['MANAGER', 'OWNER'],
    '/bug-reports/:id': ['MANAGER', 'OWNER'],
    '/canned-jobs': ['OWNER', 'MANAGER'],
    '/completed-tickets': ['OWNER', 'MANAGER', 'SERVICE_WRITER'],
    '/create-ticket': ['OWNER', 'MANAGER', 'SERVICE_WRITER'],
    // OD-34: technicians (and, via ROUTE_WORK_ROLES, detail specialists) read the shop's customers.
    '/customers': ['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN'],
    '/customers/:id': ['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN'],
    '/deleted-tickets': ['MANAGER', 'OWNER'],
    '/diagnostic-flows': ['MANAGER', 'OWNER'],
    '/dvi-intelligence': ['MANAGER', 'OWNER'],
    '/dvi-learning': ['MANAGER', 'OWNER'],
    '/enterprise/intelligence/governance': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/enterprise/talent': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/enterprise/talent/:userId': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/enterprise/talent/analytics': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/enterprise/talent/suggestions': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/enterprise/training': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/foh-queue': ['OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'],
    '/import': ['OWNER', 'MANAGER', 'SERVICE_WRITER'],
    '/import-runs': ['MANAGER', 'OWNER'],
    '/import-sources': ['MANAGER', 'OWNER'],
    '/inbox': ['OWNER', 'MANAGER', 'SERVICE_WRITER'],
    '/inspection-templates': ['OWNER', 'MANAGER', 'ORG_ADMIN'],
    '/inspection-templates/:templateId': ['OWNER', 'MANAGER', 'ORG_ADMIN'],
    '/manager-passport/:userId': ['MANAGER', 'OWNER'],
    '/optiassets': ['OWNER', 'MANAGER', 'AUDITOR', 'ORG_ADMIN'],
    '/optiboard': ['OWNER', 'MANAGER', 'SERVICE_WRITER'],
    '/optiqueue-settings': ['MANAGER', 'OWNER'],
    '/org': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/org/districts/:districtId': ['OWNER', 'ORG_ADMIN', 'MANAGER', 'REGION_MANAGER', 'DISTRICT_MANAGER'],
    '/org/intelligence': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/org/recommendations': ['OWNER', 'ORG_ADMIN', 'MANAGER'],
    '/org/regions/:regionId': ['OWNER', 'ORG_ADMIN', 'MANAGER', 'REGION_MANAGER', 'DISTRICT_MANAGER'],
    '/payroll-reports': ['MANAGER', 'OWNER'],
    '/shop-floor': ['OWNER', 'MANAGER', 'SERVICE_WRITER'],
    '/shop-floor/editor': ['MANAGER', 'OWNER'],
    '/shop-profile': ['OWNER', 'MANAGER', 'SERVICE_WRITER'],
    '/specialty-bays': ['OWNER', 'MANAGER', 'SERVICE_WRITER'],
    '/sync-vehicle-data': ['MANAGER', 'OWNER'],
    '/team-management': ['MANAGER', 'OWNER'],
    '/technicians': ['OWNER', 'MANAGER', 'SERVICE_WRITER'],
    '/technicians/:id': ['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN', 'ORG_ADMIN'],
    '/technicians/:id/edit': ['OWNER', 'MANAGER', 'SERVICE_WRITER'],
    '/user-guide': ['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN', 'ORG_ADMIN'],
  });

/** Strip a query string and trailing slash so `/?tab=dayboard` matches `/`. */
export function routePath(page: string): string {
  const withoutQuery = (page ?? '').split('?')[0];
  if (withoutQuery === '' || withoutQuery === '/') return '/';
  return withoutQuery.replace(/\/+$/, '');
}

/**
 * Find the mirrored guard for a concrete path.
 *
 * An exact key wins. Otherwise the path is matched segment-by-segment against
 * the parameterised keys, where a `:param` segment matches exactly one
 * segment of anything.
 *
 * Without this, `/technicians/abc` found no key called `/technicians/abc`,
 * fell through to "no guard", and was reported reachable by everybody —
 * while `App.tsx` guards `/technicians/:id` to four roles. No CORE-BL step
 * points at a concrete parameterised path today, so nothing was mis-answered
 * in practice; the hole was one authored step away from being live, and
 * `coreBlRouteReachability.test.ts` pins both the matching and the fact that
 * no step relies on it.
 *
 * This is deliberately NOT a router: no wildcards, no optional segments, no
 * ranking beyond "exact before parameterised". It answers the one question
 * the resolver asks.
 */
function guardFor(path: string): ReadonlyArray<AppRole> | undefined {
  const exact = ROUTE_ROLES[path];
  if (exact) return exact;

  const segments = path.split('/');
  for (const [pattern, roles] of Object.entries(ROUTE_ROLES)) {
    if (!pattern.includes('/:')) continue;
    const patternSegments = pattern.split('/');
    if (patternSegments.length !== segments.length) continue;
    const matches = patternSegments.every(
      (segment, i) => segment.startsWith(':') || segment === segments[i],
    );
    if (matches) return roles;
  }
  return undefined;
}

/**
 * Whether this role can actually open the page a step points at.
 *
 * A path with no guard in `App.tsx` is reachable — that is what having no
 * `requiredRoles` means. Because the mirror is complete rather than partial,
 * "absent from the table" now genuinely means "unguarded", instead of meaning
 * "not one of the handful of routes somebody remembered to list".
 *
 * A role of `null` is denied every guarded route: an unknown role satisfies
 * no `requiredRoles` list, which is what `ProtectedRoute` does with it.
 *
 * WHAT THIS DOES NOT MODEL. Only `requiredRoles` is mirrored. Some routes
 * carry a further gate — `/optiassets` sits inside an
 * `ExposureRouteGate feature="ui_optiAssets"`, and OptiAssets is an add-on
 * rather than a Core entitlement — so a `true` here means "role authority
 * admits them", not "this page will render for them". CORE-BL points no
 * lesson at such a route, and `coreBlRouteReachability.test.ts` checks that
 * it stays that way.
 */
// `KnownAppRole` rather than `AppRole`: STAFF is a real membership role that
// the generated types do not carry yet, and it appears in none of the route
// guards above — so it opens nothing, which is exactly the fail-closed answer
// a neutral access level should get.
export function canRoleOpen(
  page: string,
  role: KnownAppRole | null,
  workRoles?: ReadonlyArray<string> | null,
): boolean {
  const path = routePath(page);
  const allowed = guardFor(path);
  if (!allowed) return true;
  // Widened for the comparison only: a role the guards have never heard of
  // is simply not in the list, which is the fail-closed answer.
  if (role && (allowed as ReadonlyArray<string>).includes(role)) return true;
  // Core-100 R4 CORE-BL (OD-14): `ProtectedRoute` admits the UNION of the legacy
  // vocabulary — which a multi-role member reaches through their work roles
  // (`actsAsLegacyRole`: TECHNICIAN work -> TECHNICIAN, SERVICE_ADVISOR work ->
  // SERVICE_WRITER) — and the explicit `requiredWorkRoles`. A STAFF member who
  // details or turns wrenches was denied every lesson by a mirror that only
  // read the neutral STAFF scalar.
  // Array check, not `?? []`: callers pass this as an Array.prototype.filter callback,
  // where the second argument is an index.
  for (const w of Array.isArray(workRoles) ? workRoles : []) {
    const legacy = WORK_ROLE_LEGACY_PROJECTION[w];
    if (legacy && (allowed as ReadonlyArray<string>).includes(legacy)) return true;
    if ((ROUTE_WORK_ROLES[path] ?? workRoleGuardFor(path) ?? []).includes(w)) return true;
  }
  return false;
}

/**
 * Work roles each route ALSO admits (`requiredWorkRoles` in `App.tsx`),
 * mirrored like `ROUTE_ROLES` and pinned by `coreBlRouteReachability`-style
 * source scans (`coreBlDetailSpecialistTrack.test.tsx`).
 */
export const ROUTE_WORK_ROLES: Readonly<Record<string, ReadonlyArray<string>>> =
  Object.freeze({
    '/': ['DETAIL_SPECIALIST'],
    '/technicians/:id': ['DETAIL_SPECIALIST'],
    '/birdseye': ['DETAIL_SPECIALIST'],
    '/account-settings': ['DETAIL_SPECIALIST'],
    '/user-guide': ['DETAIL_SPECIALIST'],
    '/attach': ['DETAIL_SPECIALIST'],
    // OD-34: a detail specialist reads the shop's customers.
    '/customers': ['DETAIL_SPECIALIST'],
    '/customers/:id': ['DETAIL_SPECIALIST'],
  });

/** `actsAsLegacyRole`'s projection of a work role onto the legacy route vocabulary (DETAIL_SPECIALIST has none, on purpose). */
const WORK_ROLE_LEGACY_PROJECTION: Readonly<Record<string, string | undefined>> = Object.freeze({
  TECHNICIAN: 'TECHNICIAN',
  SERVICE_ADVISOR: 'SERVICE_WRITER',
  DISPATCHER: 'DISPATCHER',
  PARTS: 'PARTS',
});

function workRoleGuardFor(path: string): ReadonlyArray<string> | undefined {
  const segments = path.split('/');
  for (const [pattern, roles] of Object.entries(ROUTE_WORK_ROLES)) {
    if (!pattern.includes('/:')) continue;
    const p = pattern.split('/');
    if (p.length === segments.length && p.every((s, i) => s.startsWith(':') || s === segments[i])) return roles;
  }
  return undefined;
}

/**
 * Routes carrying a gate this mirror does NOT model, beyond `requiredRoles`.
 *
 * Mirrored from `App.tsx` alongside the role table, so a lesson pointed at
 * one of them fails a test rather than quietly promising a page that an
 * unpurchased add-on or an unset exposure flag will refuse.
 */
export const ROUTES_WITH_EXTRA_GATES: Readonly<Record<string, string>> =
  Object.freeze({
    '/optiassets': 'ExposureRouteGate ui_optiAssets (OPTIASSETS add-on)',
    // CORE-BJ replaced the shop-role guard on the support console with
    // `SupportOperatorRoute`, an internal support-operator check. These two
    // routes therefore carry NO `requiredRoles` any more, which is why they
    // left the role table above.
    //
    // Note the difference from `/optiassets`: there, role authority genuinely
    // admits the role and a purchased add-on decides rendering. Here role
    // authority admits NOBODY — no shop role opens a support console, which is
    // the whole point of the change. `canRoleOpen` cannot express that (an
    // unlisted path reads as unguarded), so the guarantee rests on this
    // registry and on the test that no lesson may point at a route listed in
    // it.
    '/support/dashboard': 'SupportOperatorRoute (internal support operator, not a shop role)',
    '/support/shops': 'SupportOperatorRoute (internal support operator, not a shop role)',
    // Core-100 R5 CORE-BJ / OD-49: the read-only view of a shop that approved support access.
    '/support/granted-shop': 'SupportOperatorRoute (internal support operator, not a shop role)',
    // WS-GAPS2 B-W11 (LEDGER W4d-3): the read-only schedule-conflicts page. No `requiredRoles`: its gate is the
    // SERVER audience predicate is_schedule_conflict_viewer (approvers + operational dispatchers), which a role
    // list cannot express. No lesson may point here.
    '/schedule-conflicts': 'ScheduleConflictViewerRoute (server predicate is_schedule_conflict_viewer)',
  });

/**
 * Roles that cannot reach the permanent reference.
 *
 * The role tracks used to tell everybody *"the user guide is in the account
 * menu and stays available"*. For DISPATCHER, PARTS, AUDITOR, REGION_MANAGER
 * and DISTRICT_MANAGER the router denies `/user-guide`, so that sentence was
 * simply false — and a promise of permanent reference is exactly the kind of
 * claim a person acts on later, alone, when they need it.
 */
export function canRoleOpenUserGuide(
  role: KnownAppRole | null,
  workRoles?: ReadonlyArray<string> | null,
): boolean {
  return canRoleOpen('/user-guide', role, Array.isArray(workRoles) ? workRoles : null);
}

/**
 * Roles holding no guarded route at all.
 *
 * Computed, not asserted. As of this mirror it is DISPATCHER and PARTS: both
 * hold real permission grants in `usePermissions` (a dispatcher may assign
 * tickets; a parts person may view them) and neither appears anywhere in
 * `App.tsx`. There is no page in the product they can open, so there is
 * nowhere to teach them — a gap in routing, not in content. Recorded as
 * CORE-BL-GAP-010.
 *
 * AUDITOR is deliberately NOT in this set, and an earlier record entry that
 * lumped it in with these two was wrong. See `rolesWithNoCoreSurface`.
 */
export function rolesWithNoGuardedRoute(
  roles: ReadonlyArray<AppRole>,
): AppRole[] {
  return roles.filter(
    (role) =>
      !Object.values(ROUTE_ROLES).some((allowed) => allowed.includes(role)),
  );
}

/**
 * Roles admitted to no route CORE-BL can teach on — a different question.
 *
 * A route counts as teachable here only if it is (a) role-admitted and (b) not
 * behind a gate this mirror does not model. That second clause is why AUDITOR
 * belongs in this set while NOT belonging in `rolesWithNoGuardedRoute`:
 *
 *   * route authority DOES admit AUDITOR — to `/optiassets`, and to nothing
 *     else;
 *   * but `/optiassets` sits inside `<ExposureRouteGate feature="ui_optiAssets">`,
 *     and `optiAssets` is an ADD-ON entitlement (`OPTIASSETS`), explicitly
 *     excluded from `PRESET_MANAGED_FEATURE_KEYS`. It is not part of Core
 *     under normal configuration.
 *
 * So authoring an auditor walkthrough there would make Core onboarding depend
 * on a purchased add-on: correct for shops that bought it, and a tour of a
 * blank page for every shop that did not. That is a worse failure than saying
 * plainly that there is nothing yet, so it was not done. The blocker is
 * product availability, not the router — recorded as CORE-BL-GAP-012, which
 * is a different gap from GAP-010 and should not be closed by the same fix.
 *
 * REGION_MANAGER and DISTRICT_MANAGER are here for a third reason again: they
 * hold only `/org/regions/:regionId` and `/org/districts/:districtId`, which
 * need a scope id an onboarding step has no way to supply.
 */
export function rolesWithNoCoreSurface(
  roles: ReadonlyArray<AppRole>,
): AppRole[] {
  return roles.filter((role) => {
    const teachable = Object.entries(ROUTE_ROLES).filter(
      ([path, allowed]) =>
        allowed.includes(role) &&
        !(path in ROUTES_WITH_EXTRA_GATES) &&
        !path.includes('/:'),
    );
    return teachable.length === 0;
  });
}
